import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

import Modal from 'react-modal'

import SandDollarIcon from '@src/components/icons/SandDollar'
import SearchIcon from '@src/components/icons/Search'
import { ChainId } from '@src/configs/chainInfo'
import { copy } from '@src/constants/application'
import { ConfigLoadingStatus } from '@src/enums'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import {
    ChainSelector,
    ChainSelectorPane,
    SearchingIndicator,
    TokenCategoriesDisplay,
    TokenResultsDisplay,
} from '@src/pages/Exchange/components/TokenSelectorModal/components'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils'
import { useConfigStore } from '@src/zustand_stores'

interface MobileTokenSelectorProps {
    tokenQuery: string
    setTokenQuery: Dispatch<SetStateAction<string>>
    searchResultsChain: Record<string, any>
    availableChainsFiltered: Record<string, any>
    selectedChain: string | number
    temporaryChainSelection?: number
    activeChainSelection?: number
    onChainClicked: (chainId: number) => void
    handleClearSearch: () => void
    handlePasteAddress: () => void
    onClose: () => void
    isSearchingAllChains: boolean
    hasNoSearchResults: boolean
    renderNoResultsFound: () => JSX.Element
    searchProgress: { completed: number; total: number }
    displayedTokens: IToken[]
    selectedToken?: IToken
    onTokenClicked: (token: IToken) => void
    getTokenBalance: (token: IToken) => { tokenBalance: string; usdBalance: string } | undefined
    searchResult: IToken[]
    side: 'buy' | 'sell'
    sellData: IExchangeData
    buyData: IExchangeData
    disabledChains?: number[]
    onTokenSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
}

const MobileTokenSelector = ({
    tokenQuery,
    setTokenQuery,
    searchResultsChain,
    availableChainsFiltered,
    selectedChain,
    temporaryChainSelection,
    activeChainSelection,
    onChainClicked,
    handleClearSearch,
    handlePasteAddress,
    onClose,
    isSearchingAllChains,
    hasNoSearchResults,
    renderNoResultsFound,
    searchProgress,
    displayedTokens,
    selectedToken,
    onTokenClicked,
    getTokenBalance,
    searchResult,
    side,
    sellData,
    buyData,
    disabledChains,
    onTokenSearchChange,
}: MobileTokenSelectorProps): JSX.Element => {
    const [isNetworkSelectorOpen, setIsNetworkSelectorOpen] = useState(false)

    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()

    const isConfigurationLoading = React.useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )

    // Handle token search change
    const handleTokenSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value

        // If the parent component provided an onTokenSearchChange function, use it
        if (onTokenSearchChange) {
            // This will properly clear chain selections in the parent component
            await onTokenSearchChange(e)

            // Always close the network selector if it's open when search changes
            if (isNetworkSelectorOpen) {
                setIsNetworkSelectorOpen(false)
            }
            return
        }

        // Fallback behavior if parent's onTokenSearchChange is not available
        // Always clear chain selections when the query changes
        if (newValue !== tokenQuery) {
            setTokenQuery(newValue)

            // Close the network selector if it's open
            if (isNetworkSelectorOpen) {
                setIsNetworkSelectorOpen(false)
            }
        } else {
            setTokenQuery(newValue)
        }
    }

    return (
        <div className='flex h-full flex-col overflow-hidden sm:hidden'>
            {/* Mobile Header with subtle title shift */}
            <div className='flex items-center justify-between border-b border-token-modal-border p-4'>
                <div className='flex items-center gap-3'>
                    {/* Back button with opacity transition */}
                    <div
                        className={`flex items-center transition-opacity duration-200 ${isNetworkSelectorOpen ? 'opacity-100' : 'opacity-0'}`}
                    >
                        <button
                            onClick={() => setIsNetworkSelectorOpen(false)}
                            className='rounded-[6px] bg-mach-gray p-1.5 text-white transition-colors hover:brightness-110'
                        >
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M15 18L9 12L15 6'
                                    stroke='white'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </button>
                    </div>
                    {/* Title with subtle shift */}
                    <h2
                        className={`text-[20px] font-semibold transition-transform duration-200 ${
                            isNetworkSelectorOpen ? '' : '-translate-x-[32px] transform'
                        }`}
                    >
                        {isNetworkSelectorOpen ? `Select ${side} network` : `Select ${side} token`}
                    </h2>
                </div>

                <button
                    onClick={onClose}
                    className='rounded-[6px] bg-mach-gray p-0.5 text-white transition-colors hover:brightness-110'
                    type='button'
                >
                    <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='scale-75 transform'
                    >
                        <path
                            d='M18 6L6 18M6 6L18 18'
                            stroke='white'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                </button>
            </div>

            <div className='relative flex-1'>
                {/* Token content - slides out to the left when network selector is open */}
                <div
                    className={`absolute inset-0 flex flex-col transition-transform duration-300 ease-in-out ${
                        isNetworkSelectorOpen ? '-translate-x-full' : 'translate-x-0'
                    }`}
                >
                    {/* Mobile Search */}
                    <div className='px-4 pb-4'>
                        <div className='flex items-center justify-between gap-3 rounded-md bg-mach-darkBlue px-3 py-2.5 transition-all duration-200'>
                            <div className='flex flex-1 items-center gap-2'>
                                <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                                <input
                                    type='text'
                                    placeholder={
                                        isConfigurationLoading
                                            ? 'Loading Configuration...'
                                            : copy.tokenSelectorPlaceholder
                                    }
                                    value={tokenQuery}
                                    onChange={handleTokenSearchChange}
                                    disabled={isConfigurationLoading}
                                    className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none disabled:cursor-not-allowed disabled:opacity-50'
                                />
                            </div>
                            {tokenQuery ? (
                                <button
                                    className='rounded-md bg-mach-gray p-1.5 text-sm !leading-4 text-white transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'
                                    onClick={handleClearSearch}
                                    disabled={isConfigurationLoading}
                                >
                                    Clear
                                </button>
                            ) : (
                                <button
                                    className='rounded-md bg-mach-gray p-1.5 text-sm !leading-4 text-white transition-colors hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50'
                                    onClick={handlePasteAddress}
                                    disabled={isConfigurationLoading}
                                >
                                    Paste address
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Show searching indicator, no results UI, or regular content */}
                    {isSearchingAllChains ? (
                        <div className='flex-1'>
                            <SearchingIndicator searchProgress={searchProgress} />
                        </div>
                    ) : hasNoSearchResults ? (
                        renderNoResultsFound()
                    ) : (
                        <>
                            {/* Networks section */}
                            <div className='w-full px-4 pb-4'>
                                <ChainSelectorPane
                                    searchResultsChain={searchResultsChain}
                                    availableChainsFiltered={availableChainsFiltered}
                                    selectedChain={selectedChain}
                                    temporaryChainSelection={temporaryChainSelection}
                                    activeChainSelection={activeChainSelection}
                                    onChainClicked={onChainClicked}
                                    tokenQuery={tokenQuery}
                                    disabledChains={disabledChains}
                                    isNetworkSelectorOpen={isNetworkSelectorOpen}
                                    setIsNetworkSelectorOpen={setIsNetworkSelectorOpen}
                                    side={side}
                                />
                            </div>

                            {/* Token sections */}
                            <div className='space-y-3 overflow-y-auto px-3'>
                                {tokenQuery ? (
                                    <TokenResultsDisplay
                                        activeChainSelection={activeChainSelection}
                                        tokenQuery={tokenQuery}
                                        availableChains={availableChainsFiltered}
                                        displayedTokens={displayedTokens}
                                        getTokenBalance={getTokenBalance}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        searchResult={searchResult}
                                        currentlySelectedToken={side === 'sell' ? sellData.token : buyData.token}
                                    />
                                ) : (
                                    <TokenCategoriesDisplay
                                        displayedTokens={displayedTokens}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        getTokenBalance={getTokenBalance}
                                        currentlySelectedToken={side === 'sell' ? sellData.token : buyData.token}
                                        temporaryChainSelection={temporaryChainSelection}
                                        activeChainSelection={activeChainSelection}
                                        selectedChain={selectedChain}
                                        tokenQuery={tokenQuery}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Network selector content - slides in from the right */}
                <div
                    className={`absolute inset-0 flex flex-col transition-transform duration-300 ease-in-out ${
                        isNetworkSelectorOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                >
                    <div className='w-full flex-1'>
                        <ChainSelectorPane
                            searchResultsChain={searchResultsChain}
                            availableChainsFiltered={availableChainsFiltered}
                            selectedChain={selectedChain}
                            temporaryChainSelection={temporaryChainSelection}
                            activeChainSelection={activeChainSelection}
                            onChainClicked={onChainClicked}
                            tokenQuery={tokenQuery}
                            disabledChains={disabledChains}
                            isNetworkSelectorOpen={isNetworkSelectorOpen}
                            setIsNetworkSelectorOpen={setIsNetworkSelectorOpen}
                            side={side}
                        />
                    </div>
                </div>
            </div>

            {/* Sand Dollars Banner - Always visible at bottom */}
            {isFeatureEnabled('SAND_DOLLARS') && (
                <div className='border-t border-token-modal-border p-4'>
                    <div className='flex items-center gap-2 rounded-[10px] bg-[#381601] p-2'>
                        <SandDollarIcon height='32' width='32' />
                        <p className='flex-1 text-xs font-medium leading-4 text-[#E4E4E4]'>
                            Earn Sand Dollars by making non duo-stablecoin swaps and receive your cashback in stablecoin
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MobileTokenSelector
